import React, { useState } from 'react';
import { useAuth } from '../AuthContext';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * One-time migration utility for workout schema normalization
 *
 * Old schema: date, durationMin
 * New schema: completedAt, minutes
 *
 * Access at: /migrate-workouts
 * Remove this page after migration is complete.
 */
function MigrateWorkouts() {
  const { user } = useAuth();
  const [status, setStatus] = useState('ready');
  const [results, setResults] = useState(null);
  const [logs, setLogs] = useState([]);

  const addLog = (message, type = 'info') => {
    setLogs(prev => [...prev, { message, type, timestamp: new Date() }]);
  };

  const migrate = async (dryRun = true) => {
    if (!user) {
      addLog('Error: Not signed in', 'error');
      return;
    }

    setStatus('running');
    setLogs([]);
    addLog(dryRun ? 'Starting DRY RUN...' : 'Starting LIVE migration...', 'info');

    try {
      const workoutsRef = collection(db, 'users', user.uid, 'workouts');
      const snapshot = await getDocs(workoutsRef);

      addLog(`Found ${snapshot.size} workout documents`, 'info');

      let migratedCount = 0;
      let skippedCount = 0;
      const changes = [];

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const docId = docSnap.id;

        // Check if doc has old schema (date but no completedAt)
        const hasOldSchema = data.date && !data.completedAt;

        if (hasOldSchema) {
          const oldDate = data.date?.toDate?.() || new Date();
          const minutes = data.durationMin || data.minutes || 0;

          changes.push({
            docId,
            focus: data.focus,
            oldDate,
            minutes,
          });

          addLog(
            `${docId}: ${data.focus} (${oldDate.toLocaleDateString()}) → completedAt + minutes=${minutes}`,
            dryRun ? 'preview' : 'success'
          );

          if (!dryRun) {
            try {
              const docRef = doc(db, 'users', user.uid, 'workouts', docId);
              await updateDoc(docRef, {
                completedAt: data.date,
                minutes: minutes,
              });
              migratedCount++;
            } catch (error) {
              addLog(`Failed to update ${docId}: ${error.message}`, 'error');
            }
          } else {
            migratedCount++;
          }
        } else {
          skippedCount++;
          const reason = data.completedAt ? 'already migrated' : 'missing date field';
          addLog(`${docId}: ${data.focus} - ${reason}`, 'skip');
        }
      }

      const summary = {
        total: snapshot.size,
        migrated: migratedCount,
        skipped: skippedCount,
        dryRun,
      };

      setResults(summary);
      addLog(`\nSummary: ${migratedCount} migrated, ${skippedCount} skipped`, 'success');

      if (dryRun && migratedCount > 0) {
        addLog('DRY RUN complete. Click "Migrate (LIVE)" to apply changes.', 'warning');
      } else if (!dryRun && migratedCount > 0) {
        addLog('Migration complete! Refresh to see updated history.', 'success');
      }

      setStatus('complete');
    } catch (error) {
      addLog(`Migration failed: ${error.message}`, 'error');
      setStatus('error');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-dark-bg flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-light-muted dark:text-dark-muted font-plex">Please sign in first</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg p-4">
      <div className="max-w-3xl mx-auto py-8">
        <div className="bg-light-surface dark:bg-dark-surface rounded-[18px] p-6 mb-6">
          <h1 className="text-[24px] font-grotesk font-bold text-light-ink dark:text-dark-ink mb-2">
            Workout Schema Migration
          </h1>
          <p className="text-[15px] font-plex text-light-muted dark:text-dark-muted mb-4">
            This tool migrates workout documents from the old schema (date, durationMin) to the new
            schema (completedAt, minutes).
          </p>

          <div className="bg-light-bg dark:bg-dark-bg rounded-[12px] p-4 mb-6">
            <h2 className="text-[13px] font-plex font-semibold text-light-ink dark:text-dark-ink mb-2 uppercase tracking-wider">
              Why this is needed
            </h2>
            <ul className="text-[14px] font-plex text-light-muted dark:text-dark-muted space-y-1 list-disc list-inside">
              <li>Old workout docs use different field names than current code</li>
              <li>History.js queries completedAt, so old docs are invisible</li>
              <li>This migration makes all workouts visible again</li>
            </ul>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => migrate(true)}
              disabled={status === 'running'}
              className="px-6 py-3 rounded-[14px] bg-light-bg dark:bg-dark-bg border-2 border-light-border dark:border-dark-border text-light-ink dark:text-dark-ink font-plex font-semibold text-[15px] hover:border-light-accent dark:hover:border-dark-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Preview (Dry Run)
            </button>
            <button
              onClick={() => migrate(false)}
              disabled={status === 'running'}
              className="px-6 py-3 rounded-[14px] bg-light-accent dark:bg-dark-accent text-white font-plex font-semibold text-[15px] hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Migrate (LIVE)
            </button>
          </div>
        </div>

        {results && (
          <div className="bg-light-surface dark:bg-dark-surface rounded-[18px] p-6 mb-6">
            <h2 className="text-[17px] font-grotesk font-bold text-light-ink dark:text-dark-ink mb-4">
              Results
            </h2>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-[28px] font-grotesk font-bold text-light-ink dark:text-dark-ink tabular-nums">
                  {results.total}
                </div>
                <div className="text-[13px] font-plex text-light-muted dark:text-dark-muted">Total</div>
              </div>
              <div>
                <div className="text-[28px] font-grotesk font-bold text-light-accent dark:text-dark-accent tabular-nums">
                  {results.migrated}
                </div>
                <div className="text-[13px] font-plex text-light-muted dark:text-dark-muted">Migrated</div>
              </div>
              <div>
                <div className="text-[28px] font-grotesk font-bold text-light-muted dark:text-dark-muted tabular-nums">
                  {results.skipped}
                </div>
                <div className="text-[13px] font-plex text-light-muted dark:text-dark-muted">Skipped</div>
              </div>
            </div>
          </div>
        )}

        {logs.length > 0 && (
          <div className="bg-light-surface dark:bg-dark-surface rounded-[18px] p-6">
            <h2 className="text-[17px] font-grotesk font-bold text-light-ink dark:text-dark-ink mb-4">
              Log
            </h2>
            <div className="bg-light-bg dark:bg-dark-bg rounded-[12px] p-4 font-mono text-[12px] max-h-[400px] overflow-y-auto space-y-1">
              {logs.map((log, idx) => {
                const colors = {
                  info: 'text-light-ink dark:text-dark-ink',
                  success: 'text-green-600 dark:text-green-400',
                  error: 'text-red-600 dark:text-red-400',
                  warning: 'text-orange-600 dark:text-orange-400',
                  preview: 'text-blue-600 dark:text-blue-400',
                  skip: 'text-light-muted dark:text-dark-muted',
                };
                return (
                  <div key={idx} className={colors[log.type] || colors.info}>
                    {log.message}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MigrateWorkouts;
